import { Injectable } from '@angular/core';
import { ArticleVO } from '@article/model/vo/article.vo';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { BaseArticleProjectProcessorFactory } from '@article/model/base-article-project-processor-factory';
import { ProjectModel } from '@project/model/project.model';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ArrayUtils } from '@shared/util/array-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionProjectData } from '@article/model/connection-project-data';
import { ArticleCreationManagerFactory } from '@project/components/article/manager/article-creation.manager.factory';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { SuitableConnectionAreasVO } from '@project/components/article/model/vo/suitable-connection-areas.vo';

@Injectable({
  providedIn: 'root'
})
export class AddArticleService {

  constructor(private readonly mountingRailHelper: MountingRailHelper,
              private readonly projectModel: ProjectModel,
              private readonly baseArticleProjectProcessorFactory: BaseArticleProjectProcessorFactory,
              private readonly undoRedoHelper: UndoRedoHelper,
              private readonly articleCreationManagerFactory: ArticleCreationManagerFactory,
  ) {
  }

  public async addArticlesToMountingRail(articles: ArticleVO[]): Promise<string[]> {
    if (ArrayUtils.isEmpty(articles)) {
      return [];
    }
    const rangeOnLineArticleDatas = this.mountingRailHelper.createRangeOnLineArticleDataBySelection();
    let articleProjectProcessor = this.baseArticleProjectProcessorFactory.createBaseArticleManagerByProjectType(this.projectModel.currentProjectType, 'addArticleProcess');
    const articleGuids: string[] = [];
    let insertedArticleData = await articleProjectProcessor.addArticleByProjectData(rangeOnLineArticleDatas[0], articles[0]);
    if (insertedArticleData.createUndoOperation) {
      this.undoRedoHelper.pushInsertArticleByLocationUndoOperation(insertedArticleData);
    }
    articleGuids.push(insertedArticleData.baseArticleGUID);
    for (let article of articles.slice(1)) {
      insertedArticleData = await articleProjectProcessor.addArticleByNeighbor(insertedArticleData.baseArticleGUID, article);
      if (insertedArticleData.createUndoOperation) {
        this.undoRedoHelper.pushInsertArticleByLocationUndoOperation(insertedArticleData);
      }
      articleGuids.push(insertedArticleData.baseArticleGUID);
    }
    return articleGuids;
  }

  public async addArticleToConnections(suitableConnectionAreas: SuitableConnectionAreasVO, connections: Connection[]): Promise<ArticleContainer[]> {
    const connectionProjectData = new ConnectionProjectData();
    connectionProjectData.connectionCubes = connections;
    const articleCreationManager = this.articleCreationManagerFactory.getNewInstance();
    articleCreationManager.showFaultMessage = false;
    await articleCreationManager.createArticlesBySuitableGivingConnectionAreasAndAcceptingCubes(suitableConnectionAreas, connections);
    if (articleCreationManager.createdUndoOperations && articleCreationManager.createdUndoOperations.length > 0) {
      for (const undoOperation of articleCreationManager.createdUndoOperations) {
        await this.undoRedoHelper.pushUndoOperation(undoOperation);
      }
    }
    return articleCreationManager.getCreatedArticleContainers();
  }
}
